import {Arr, Bool, Num, Obj, Opt, OptBool, OptStr, Str} from '@repo/shared/api/core/api_schema';
import type {SchemaToType} from '@repo/shared/api/core/api_schema';
import {TV_CATALOGUE_API} from '@repo/shared/api/tv_catalogue';
import {TV_KOH_LANTA_API} from '@repo/shared/api/tv_koh_lanta';
import {TvPreferencesSchema} from '@repo/shared/api/tv_preferences';
import type {TvPreferenceLanguage} from '@repo/shared/api/tv_preferences';
import {TV_SYNOPSIS_API} from '@repo/shared/api/tv_synopsis';
import {TV_TRAILER_API} from '@repo/shared/api/tv_trailer';

export type TvDeviceStatus = 'pending' | 'approved' | 'rejected' | 'revoked';

export const TvAccountSchema = Obj({
  email: Str(),
  createdAt: Num(),
  preferredAudioLanguage: OptStr<TvPreferenceLanguage>(),
  preferredSubtitleLanguage: OptStr<TvPreferenceLanguage>(),
});
export type TvAccount = SchemaToType<typeof TvAccountSchema>;

export const TvDeviceSchema = Obj({
  id: Str(),
  requestCode: Str(),
  name: Str(),
  status: Str<TvDeviceStatus>(),
  email: OptStr(),
  createdAt: Num(),
  updatedAt: Num(),
});
export type TvDevice = SchemaToType<typeof TvDeviceSchema>;

export const TvStatusSchema = Obj({
  device: TvDeviceSchema,
  account: Opt(TvAccountSchema),
  pollAfterSeconds: Num(),
});
export type TvStatus = SchemaToType<typeof TvStatusSchema>;

// Device credentials are carried only in Authorization headers, never in API payloads or URLs.
export const TV_API = {
  ...TV_CATALOGUE_API,
  ...TV_SYNOPSIS_API,
  ...TV_TRAILER_API,
  ...TV_KOH_LANTA_API,
  // Older installed clients reject extra response keys. Preferences are returned only on opt-in.
  '/register-device': {
    POST: {req: Obj({name: Str(), includePreferences: OptBool()}), res: TvStatusSchema},
  },
  '/device-status': {POST: {req: Obj({includePreferences: OptBool()}), res: TvStatusSchema}},
  '/session': {
    POST: {
      req: Obj({includePreferences: OptBool()}),
      res: Obj({account: TvAccountSchema, device: TvDeviceSchema}),
    },
  },
  '/account/preferences': {POST: {req: TvPreferencesSchema, res: Obj({account: TvAccountSchema})}},
  '/logout': {POST: {req: Obj({}), res: Obj({loggedOut: Bool()})}},
};

export const TV_ADMIN_API = {
  '/admin/tv/list': {
    POST: {
      req: Obj({
        kind: Str<'account' | 'device'>(),
        paginationToken: OptStr(),
        includePreferences: OptBool(),
      }),
      res: Obj({
        accounts: Arr(TvAccountSchema),
        devices: Arr(TvDeviceSchema),
        paginationToken: OptStr(),
      }),
    },
  },
  '/admin/tv/create-account': {
    POST: {req: Obj({email: Str()}), res: Obj({account: TvAccountSchema})},
  },
  '/admin/tv/preferences': {
    POST: {
      req: Obj({email: Str(), ...TvPreferencesSchema.properties}),
      res: Obj({account: TvAccountSchema}),
    },
  },
  '/admin/tv/approve': {POST: {req: Obj({deviceId: Str(), email: Str()}), res: Obj({})}},
  '/admin/tv/reject': {POST: {req: Obj({deviceId: Str()}), res: Obj({})}},
  '/admin/tv/revoke-device': {POST: {req: Obj({deviceId: Str()}), res: Obj({})}},
  '/admin/tv/logout-account': {POST: {req: Obj({email: Str()}), res: Obj({})}},
};
